/* eslint-disable camelcase */

import { type CommentAttachment, type CommentData, removeThumbsupComment, thumbsupComment } from 'Api/comments';
import 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';
import type { IconName } from 'Components/vm-icon/vm-icon';
import 'Components/vm-label/vm-label';
import 'Components/vm-profile-image/vm-profile-image';
import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { getAgo } from 'Utils/text';
import 'Video/components/vm-video-comment-attachment-link/vm-video-comment-attachment-link';
import 'Video/components/vm-video-comment-attachment-thumbnail/vm-video-comment-attachment-thumbnail';
import 'Video/components/vm-video-comment-composer/vm-video-comment-composer';
import Styles from './vm-video-comments-list-item.scss';

/**
 * A single comment row inside a `<vm-video-comments-list>`, used recursively for
 * nested replies via the bound comment's `children`.
 *
 * Replying differs by viewport: desktop announces `-reply-open` and renders its
 * own inline composer when the organism puts this id in `openReplies`; mobile
 * announces `vm-video-comment-reply:select` for the sheet composer instead.
 *
 * @csspart author - Author name link to the author's profile. Style with `::part(author)`.
 *
 * @fires vm-video-comments-list-item:reply-open - bubbles + composed. Detail:
 *   `{ parentId: number }`. A reply composer for `parentId` has been requested.
 * @fires vm-video-comments-list-item:reply-close - bubbles + composed. Detail:
 *   `{ parentId: number }`. The reply composer for `parentId` should no longer
 *   render.
 */
@customElement('vm-video-comments-list-item')
export class VmVideoCommentsListItem extends LitElement {
    static override styles = Styles;

    @property({ type: Object }) comment: CommentData | null = null;
    @property({ type: Boolean }) dataAddCommentsPerm = false;
    @property({ type: String }) videoSlug = '';
    @property({ type: Boolean }) isReply = false;

    @property({ type: String }) userAvatarSrc = '';
    @property({ type: String }) userAvatarName = '';
    @property({ type: String }) userProfileUrl = '';

    /** `attribute: false` because a `Set` can't round-trip through an HTML
     * attribute — Lit's Object converter would `JSON.parse` a stray `openreplies`
     * attribute and hand back `null`, blowing up `.has()`. */
    @property({ attribute: false }) openReplies: Set<number> = new Set();

    /** Threaded down from the organism so the page shares one `matchMedia`
     * subscription instead of one per item. */
    @property({ type: Boolean }) isDesktop = false;

    handleReplyClick() {
        if (!this.comment) return;

        // Mobile: defer to the organism (top composer + chip flow).
        if (!this.isDesktop) {
            const { id, author } = this.comment;
            this.dispatchEvent(
                new CustomEvent('vm-video-comment-reply:select', {
                    detail: { commentId: id, authorName: `${author.first_name} ${author.last_name}` },
                    bubbles: true,
                    composed: true,
                }),
            );
            return;
        }

        // Desktop: ask the organism to add this comment to its open-replies Set.
        this.dispatchEvent(
            new CustomEvent('vm-video-comments-list-item:reply-open', {
                detail: { parentId: this.comment.id },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _emitReplyClose(parentId: number): void {
        this.dispatchEvent(
            new CustomEvent('vm-video-comments-list-item:reply-close', {
                detail: { parentId },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _onInlineComposerCancel = (e: Event) => {
        // The organism's `vm-comment-reply-cancel` handler is for the mobile chip;
        // inline cancel is announced with `-reply-close` instead.
        e.stopPropagation();
        if (this.comment) this._emitReplyClose(this.comment.id);
    };

    commentPublishedDate(comment: CommentData): string {
        return getAgo(comment.when);
    }

    handleThumbsUpClick() {
        if (!this.comment) return;

        const { id, user_has_thumbed_up, thumbs_up_count } = this.comment;
        const toggle = user_has_thumbed_up ? removeThumbsupComment : thumbsupComment;

        // Mutate in place to avoid triggering parent re-render overwriting child state
        this.comment.user_has_thumbed_up = !user_has_thumbed_up;
        this.comment.thumbs_up_count = !user_has_thumbed_up
            ? (thumbs_up_count || 0) + 1
            : Math.max((thumbs_up_count || 0) - 1, 0);
        this.requestUpdate();

        const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
        if (context?.videoId) {
            window.vumedi?.mixpanel?.track('VideoCommentThumbupToggled', {
                video_id: context.videoId,
                video_slug: context.videoSlug,
                channel_id: context.channelId,
                comment_id: id,
                comment_author_id: this.comment.author?.id || null,
                thumbup_enabled: !user_has_thumbed_up,
                template_version: context.templateVersion,
            });
        }

        // No Sentry call — `client.ts`'s `beforeError` already reports it.
        toggle({ videoSlug: this.videoSlug, commentId: id }).catch(() => {
            if (!this.comment) return;
            this.comment.user_has_thumbed_up = user_has_thumbed_up;
            this.comment.thumbs_up_count = thumbs_up_count;
            this.requestUpdate();
        });
    }

    renderAttachments(attachments: CommentAttachment[]) {
        const attachmentItems = attachments.map((attachment) => {
            if (attachment.mimetype.includes('image')) {
                return html`
                    <vm-video-comment-attachment-thumbnail
                        src=${attachment.url}
                        alt="Comment attachment"
                    ></vm-video-comment-attachment-thumbnail>
                `;
            }
            return html`<vm-video-comment-attachment-link href=${attachment.url}></vm-video-comment-attachment-link>`;
        });

        return html`<div class="comment__attachments-list">${attachmentItems}</div>`;
    }

    renderReplies() {
        if (!this.comment?.children?.length) return nothing;

        return html`
            <ul class="comment__replies-list">
                ${repeat(
                    this.comment.children,
                    (child) => child.id,
                    (child) => html`
                        <vm-video-comments-list-item
                            .comment=${child}
                            .dataAddCommentsPerm=${this.dataAddCommentsPerm}
                            .videoSlug=${this.videoSlug}
                            .isReply=${true}
                            .userAvatarSrc=${this.userAvatarSrc}
                            .userAvatarName=${this.userAvatarName}
                            .userProfileUrl=${this.userProfileUrl}
                            .openReplies=${this.openReplies}
                            .isDesktop=${this.isDesktop}
                        ></vm-video-comments-list-item>
                    `,
                )}
            </ul>
        `;
    }

    private _renderInlineComposer() {
        // Desktop only — the inline composer has no mobile design.
        if (!this.comment || !this.openReplies.has(this.comment.id) || !this.isDesktop) return nothing;
        return html`
            <vm-video-comment-composer
                class="comment__inline-composer"
                variant="inline"
                video-slug=${this.videoSlug}
                avatar-src=${this.userAvatarSrc}
                avatar-name=${this.userAvatarName}
                avatar-link=${this.userProfileUrl}
                .replyToId=${this.comment.id}
                @vm-comment-reply-cancel=${this._onInlineComposerCancel}
            ></vm-video-comment-composer>
        `;
    }

    private _renderAction(label: string, onClick: () => void, iconName?: IconName) {
        return html`
            <vm-button class="comment__action" variant="ghost" color-scheme="neutral" size="sm" @click=${onClick}>
                ${iconName ? html`<vm-icon slot="icon-leading" name=${iconName}></vm-icon>` : nothing} ${label}
            </vm-button>
        `;
    }

    override render() {
        const comment = this.comment;
        // A parent that mounts this before its fetch resolves would otherwise crash
        // on `comment.author.avatar` during the intermediate render.
        if (!comment?.author) return nothing;

        const fullName = `${comment.author.first_name} ${comment.author.last_name}`;
        const thumbsUpLabel = comment.thumbs_up_count > 0 ? `${comment.thumbs_up_count} Thumbs up` : 'Thumbs up';
        const thumbsUpIcon: IconName = comment.user_has_thumbed_up ? 'thumbs-up-solid' : 'thumbs-up';

        return html`
            <li class="comment ${this.isReply ? 'comment--reply' : ''}">
                <div class="comment__inner-wrap">
                    <div class="comment__profile-image">
                        <vm-profile-image
                            .imageSrc=${comment.author.avatar || ''}
                            .imageAlt=${fullName}
                            .userName=${fullName}
                            .link=${comment.author.url}
                        ></vm-profile-image>
                    </div>
                    <div class="comment__content-wrap">
                        <div class="comment__text">
                            <div class="comment__header">
                                <a href=${comment.author.url} part="author" class="comment__author">${fullName}</a>
                                ${comment.is_presenter ? html`<vm-label>Presenter</vm-label>` : nothing}
                                <time class="comment__timestamp" datetime=${comment.when}>
                                    ${this.commentPublishedDate(comment)}
                                </time>
                            </div>
                            <p class="comment__body">${comment.comment}</p>
                            ${comment.attachments?.length ? this.renderAttachments(comment.attachments) : nothing}
                        </div>
                        <div class="comment__footer">
                            ${this._renderAction(thumbsUpLabel, () => this.handleThumbsUpClick(), thumbsUpIcon)}
                            ${this.dataAddCommentsPerm
                                ? this._renderAction('Reply', () => this.handleReplyClick())
                                : nothing}
                        </div>
                        ${this._renderInlineComposer()}
                    </div>
                </div>
                ${this.renderReplies()}
            </li>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comments-list-item': VmVideoCommentsListItem;
    }
    interface HTMLElementEventMap {
        'vm-video-comment-reply:select': CustomEvent<{ commentId: number; authorName: string }>;
        'vm-video-comments-list-item:reply-open': CustomEvent<{ parentId: number }>;
        'vm-video-comments-list-item:reply-close': CustomEvent<{ parentId: number }>;
    }
}
